import { ReactNode, useEffect, useRef } from "react";
import { CheckIcon } from "@heroicons/react/16/solid";
import { cn } from "@/lib/utils";

export type FormSectionProps = {
  step: number;
  title: string;
  description?: ReactNode;
  isComplete?: boolean;
  /**
   * Brings the section into view and focuses its first input when it mounts —
   * pass this for sections that appear part-way through filling out the form,
   * so the next thing to fill in is where the user is already looking.
   */
  focusOnMount?: boolean;
  className?: string;
  children: ReactNode;
};

export function FormSection({
  step,
  title,
  description,
  isComplete = false,
  focusOnMount = false,
  className,
  children,
}: FormSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusOnMount || !sectionRef.current) {
      return;
    }

    const section = sectionRef.current;

    section.scrollIntoView({ block: "nearest", behavior: "smooth" });

    // The control that revealed this section (a date picker, a listbox) hands
    // focus back to itself as it closes, so take focus after it has settled.
    const timeout = setTimeout(() => {
      const activeElement = document.activeElement;
      const isTyping =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement;

      // A section can appear on the first keystroke of the field before it —
      // "1" in a rate is a valid amount but rarely the whole one. Never take
      // focus off a field that is still being typed into.
      if (isTyping) {
        return;
      }

      const inputs = Array.from(
        section.querySelectorAll<HTMLInputElement>(
          "input:not([disabled]):not([type=hidden]), textarea",
        ),
      );

      // The first thing left to fill in, not just the first field — sections
      // often open with something already defaulted.
      (inputs.find((input) => input.value === "") ?? inputs[0])?.focus();
    }, 150);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div ref={sectionRef} className={cn("space-y-4", className)}>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "flex size-5 items-center justify-center rounded-full text-xs font-medium",
            isComplete
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500",
          )}
        >
          {isComplete ? <CheckIcon className="size-3" /> : step}
        </span>
        <h4 className="text-sm font-medium text-gray-900">{title}</h4>
        {description ? (
          <span className="truncate text-sm text-gray-500">{description}</span>
        ) : null}
      </div>

      {children}
    </div>
  );
}
